import { useCallback, useEffect, useRef, useState } from "react";

/** FW21 (D-487, D-528 A10): die Choreografie des Fristen-Moduls (Formen, Säulen, Summe „noch offen“) startet erst, wenn das
 *  Modul zu mindestens 60 % im Bild ist, einmal je vollem Auftritt (der Merker „voll“ gilt je Sitzung). Werte wie in der
 *  Einführung (`einfuehrung/zeitplan.ts` aus W82-Web-4b; der Architekt führt beides nach dem Merge zusammen). */
export const SICHTBAR_SCHWELLE = 0.6;
/** Versatz der Säulen je Woche, beide lebhaften Fassungen (welt.css rechnet mit demselben Wert) */
export const SAEULEN_VERSATZ = 100;
/** jede Säule folgt ihrer Form um 120 ms (Vorlage `modulChoreo`): nach dem Sichtbarwerden spielt die erste Form ab Null, die erste Säule bei 120 ms */
export const SAEULE_NACH_FORM = 120;
/** die Summe zählt 130 ms nach der ersten Säule los (760 ms `expo`, `Zahl` mit `hochzaehlen`) */
export const ZAEHLEN_NACH_SAEULE = 130;
/** Start der ersten Säule ab dem vollen Auftritt der Startseite, wenn das Modul gleich im Bild ist (wie bisher in welt.css) */
export const SAEULEN_AB = { ueberall: 1500, erklaeren: 760 } as const;
/** Ende der Choreografie nach der ersten Säule: vier weitere Wochen, 900 ms Wachsen, etwas Luft */
const DAUER_MODUL = 4 * SAEULEN_VERSATZ + 900 + 100;
const STUFEN = Array.from({ length: 21 }, (_, i) => i / 20);

/** Anteil des Elements im Bild, ohne seine eigene Einschwebe-Verschiebung (`translate` des Auftritts, 26 px): gezählt wird
 *  die Lage, an der das Modul steht, nicht die, aus der es gerade kommt. */
export function sichtbarAnteil(el: Element): { anteil: number; hoehe: number } {
  const r = el.getBoundingClientRect();
  const dy = Number.parseFloat(getComputedStyle(el).translate.split(" ")[1] ?? "0") || 0;
  const hoehe = Math.max(0, Math.min(innerHeight, r.bottom - dy) - Math.max(0, r.top - dy));
  return { anteil: r.height > 0 ? hoehe / r.height : 0, hoehe };
}

/** 60 % des Moduls im Bild; ein Modul, das höher ist als das Fenster (große Schrift), gilt ab 60 % der Fensterhöhe */
export function sichtbarGenug(el: Element): boolean {
  const { anteil, hoehe } = sichtbarAnteil(el);
  return anteil >= SICHTBAR_SCHWELLE || hoehe >= SICHTBAR_SCHWELLE * innerHeight;
}

/** Beginn der ersten sichtbaren Säule auf der Zeitleiste (ms), sobald ihre CSS-Animation wirklich läuft (beim ersten Laden
 *  setzt Chromium die Startzeit erst nach dem ersten Bild); leere Wochen zählen nicht (wie `erste` in `modulChoreo` der
 *  Vorlage: nur Säulen mit Segmenten), ohne Säule `t` ms ab jetzt. Bezug für das Zählen der Summe. */
export async function ersteSaeule(el: Element | null, t: number): Promise<number> {
  const fuellungen = [...(el?.closest(".fristen-modul")?.querySelectorAll(".fuell") ?? [])];
  const a = (fuellungen.find((f) => f.childElementCount > 0) ?? fuellungen[0])?.getAnimations()[0];
  if (!a) return (document.timeline.currentTime as number | null ?? performance.now()) + t;
  await a.ready;
  return (a.startTime as number) + Number(a.effect?.getTiming().delay ?? 0);
}

export type ModulZustand = "offen" | "wartet" | "laeuft" | "fertig";
/** W82 Design-Web-1: „halten“ während der Eröffnung, „ende“ nach ihrem Schnitt durch Tipp oder Taste, sonst „frei“. */
export type ModulHalt = "frei" | "halten" | "ende";
export type ModulAuftritt = {
  zustand: ModulZustand;
  /** Verzögerung der ersten Säule ab dem Start (ms) */
  t: number;
  ref: (el: HTMLElement | null) => (() => void) | undefined;
  /** Unterbrechen-Regel: ein Tipp während der Choreografie setzt den Endzustand; ein wartendes Modul wartet weiter */
  abbrechen: () => void;
};

/** Start des Fristen-Moduls: steht es beim Einhängen zu 60 % im Bild, läuft alles wie bisher (erste Säule `basis` ms nach
 *  dem Auftritt), sonst wartet es (`data-wartet`), bis ein `IntersectionObserver` es zu 60 % im Bild meldet, dann startet die
 *  erste Säule sofort; danach ist der Beobachter abgehängt. Ohne `IntersectionObserver` startet es sofort. */
export function useModulAuftritt(aktiv: boolean, basis: number, halt: ModulHalt = "frei"): ModulAuftritt {
  const [lage, setLage] = useState<{ zustand: ModulZustand; t: number; ab: number | null }>(() => ({ zustand: aktiv ? "offen" : "fertig", t: 0, ab: null }));
  // Design-Web-1: ob das Modul beim Einhängen im Bild stand (wie im Bestand entscheidet diese Lage, ob es mit dem Auftritt spielt)
  const imBildBeimEinhaengen = useRef<boolean | null>(null);

  const ref = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      // W82 Design-Web-1 (DWW-1): die Eröffnung hält das Modul (Säulen auf 0), bis es einschwebt; danach gilt FW21 wie bisher,
      // `basis` zählt dann ab dem Einschweben. Schneidet ein Tipp oder eine Taste die Eröffnung, steht ein Modul im Bild sofort
      // fertig (wie `abbrechen` im Bestand), eines unter dem Rand wartet weiter auf 60 %
      if (imBildBeimEinhaengen.current === null) imBildBeimEinhaengen.current = typeof IntersectionObserver === "undefined" || sichtbarGenug(el);
      if (halt === "halten") {
        if (lage.zustand === "offen") setLage({ zustand: "wartet", t: 0, ab: null });
        return;
      }
      if (halt === "ende" && (lage.zustand === "offen" || lage.zustand === "wartet") && imBildBeimEinhaengen.current) {
        el.removeAttribute("data-wartet");
        setLage((l) => ({ ...l, zustand: "fertig" }));
        return;
      }
      const ab = lage.ab ?? performance.now();
      const starten = () => {
        const t = Math.round(Math.max(SAEULE_NACH_FORM, basis - (performance.now() - ab)));
        // gleich am Element starten, nicht erst nach dem Neuzeichnen der Startseite (unter Last 50 ms und mehr); React zieht
        // denselben Zustand nach
        el.removeAttribute("data-wartet");
        el.setAttribute("data-choreo", "");
        el.style.setProperty("--modul-t", `${t}ms`);
        setLage((l) => (l.zustand === "offen" || l.zustand === "wartet" ? { zustand: "laeuft", t, ab } : l));
      };
      if (lage.zustand !== "offen" && lage.zustand !== "wartet") return;
      if (typeof IntersectionObserver === "undefined" || sichtbarGenug(el) || (lage.zustand === "wartet" && lage.ab === null && imBildBeimEinhaengen.current)) {
        // im selben Commit wie der Auftritt: die Säulen hängen an derselben Stiltabelle wie bisher, also genau `basis` (die
        // Messung oben erzwingt ein Layout, unter Last bis 40 ms, das darf nicht abgezogen werden)
        if (lage.zustand === "offen") setLage({ zustand: "laeuft", t: basis, ab });
        else starten();
        return;
      }
      if (lage.zustand === "offen") setLage({ zustand: "wartet", t: 0, ab });
      const beob = new IntersectionObserver(() => {
        if (!sichtbarGenug(el)) return;
        beob.disconnect();
        starten();
      }, { threshold: STUFEN });
      beob.observe(el);
      return () => beob.disconnect();
    },
    [lage.zustand, lage.ab, basis, halt],
  );

  useEffect(() => {
    if (lage.zustand !== "laeuft") return;
    const uhr = setTimeout(() => setLage((l) => ({ ...l, zustand: "fertig" })), lage.t + DAUER_MODUL);
    return () => clearTimeout(uhr);
  }, [lage.zustand, lage.t]);

  const abbrechen = useCallback(() => setLage((l) => (l.zustand === "laeuft" ? { ...l, zustand: "fertig" } : l)), []);
  return { zustand: lage.zustand, t: lage.t, ref, abbrechen };
}
